import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Sidebar from '../components/ui/Sidebar';
import Icon from '../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import adminService from '../../../services/adminService';
import { useAuth } from '../../../contexts/AuthContext';

const SettingsAdmins = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const canManageAdmins = userProfile?.isSuperAdmin === true;

  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [canManageFromApi, setCanManageFromApi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promoteError, setPromoteError] = useState(null);
  const [promoteSuccess, setPromoteSuccess] = useState(null);
  const [demoteTarget, setDemoteTarget] = useState(null);
  const [demoteLoading, setDemoteLoading] = useState(false);

  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const fetchUsers = useCallback(async () => {
    const query = searchQueryRef.current?.trim() ?? '';
    setLoading(true);
    setError(null);
    try {
      const params = query ? { search: query } : {};
      const { data } = await adminService.getUsers(params);
      setUsers(data.users || []);
      setCanManageFromApi(data.canManageAdmins === true);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('فشل في تحميل قائمة المستخدمين');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch users: immediately when empty, debounced when searching
  useEffect(() => {
    if (!searchQuery?.trim()) {
      fetchUsers();
      return;
    }
    const timer = setTimeout(() => fetchUsers(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchUsers]);

  useEffect(() => {
    document.title = 'إدارة المشرفين - Dawayer';
  }, []);

  const handlePromote = async (e) => {
    e.preventDefault();
    const email = promoteEmail?.trim();
    if (!email) {
      setPromoteError('يرجى إدخال البريد الإلكتروني');
      return;
    };

    setPromoteLoading(true);
    setPromoteError(null);
    setPromoteSuccess(null);
    try {
      await adminService.promoteAdmin(email);
      setPromoteSuccess(`تمت إضافة ${email} كمشرف بنجاح`);
      setPromoteEmail('');
      fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.message || 'فشل في إضافة المشرف';
      setPromoteError(msg);
    } finally {
      setPromoteLoading(false);
    }
  };

  const handleDemoteClick = (u) => {
    setDemoteTarget(u);
  };

  const handleDemoteConfirm = async () => {
    if (!demoteTarget) return;

    setDemoteLoading(true);
    try {
      await adminService.demoteAdmin(demoteTarget.id);
      setDemoteTarget(null);
      fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.message || 'فشل في إزالة المشرف';
      alert(msg);
    } finally {
      setDemoteLoading(false);
    }
  };

  const handleDemoteCancel = () => {
    setDemoteTarget(null);
  };

  const showManageActions = canManageAdmins || canManageFromApi;

  return (
    <div className="min-h-screen bg-[#F9F7F2]" dir="rtl">
      <Sidebar />
      <main className="lg:mr-60 transition-default">
        <div className="p-6 lg:p-8">
          <button
            onClick={() => navigate('/admin/settings')}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-default mb-6 group"
          >
            <Icon name="ChevronRight" size={20} color="currentColor" />
            <span className="text-sm font-body">العودة إلى الإعدادات</span>
          </button>

          <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
            إدارة المشرفين
          </h1>
          <p className="text-base font-body text-muted-foreground mb-8">
            عرض وإدارة المشرفين والمستخدمين
          </p>

          {showManageActions && (
            <div className="mb-8 p-6 rounded-2xl bg-card shadow-warm border border-border max-w-xl">
              <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                إضافة مشرف جديد
              </h2>
              <p className="text-sm font-body text-muted-foreground mb-4">
                أدخل البريد الإلكتروني لمستخدم مسجّل لترقيته إلى مشرف
              </p>
              <form onSubmit={handlePromote} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="example@email.com"
                    value={promoteEmail}
                    onChange={(e) => {
                      setPromoteEmail(e.target.value);
                      setPromoteError(null);
                      setPromoteSuccess(null);
                    }}
                    error={promoteError}
                    disabled={promoteLoading}
                    className="w-full"
                  />
                </div>
                <Button
                  type="submit"
                  loading={promoteLoading}
                  iconName="UserPlus"
                  size="default"
                  disabled={promoteLoading}
                >
                  إضافة مشرف
                </Button>
              </form>
              {promoteSuccess && (
                <p className="mt-3 text-sm text-success font-body">{promoteSuccess}</p>
              )}
            </div>
          )}

          {!showManageActions && (
            <div className="mb-6 p-4 rounded-xl bg-muted/50 border border-border">
              <p className="text-sm font-body text-muted-foreground">
                يمكنك عرض قائمة المشرفين فقط. لا يمكنك إضافة أو إزالة المشرفين.
              </p>
            </div>
          )}

          {/* Search users */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                <Icon name="Search" size={18} color="currentColor" />
              </span>
              <Input
                type="search"
                placeholder="البحث بالاسم أو البريد الإلكتروني..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), fetchUsers())}
                className="w-full pr-10 pl-4"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
              <p className="text-muted-foreground font-body">{error}</p>
              <button onClick={fetchUsers} className="mt-4 text-primary hover:underline font-body">
                إعادة المحاولة
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-body text-muted-foreground">لا يوجد مستخدمون</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-card shadow-warm border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-right py-4 px-4 font-heading font-semibold text-foreground">
                        المستخدم
                      </th>
                      <th className="text-left py-4 px-4 font-heading font-semibold text-foreground">
                        البريد الإلكتروني
                      </th>
                      <th className="text-center py-4 px-4 font-heading font-semibold text-foreground">
                        الدور
                      </th>
                      {showManageActions && (
                        <th className="text-left py-4 px-4 font-heading font-semibold text-foreground">
                          إجراءات
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr
                        key={u.id}
                        className="border-b border-border last:border-b-0 hover:bg-muted/20 transition-default"
                      >
                        <td className="py-4 px-4 text-right">
                          <Link
                            to={`/user/${u.id}`}
                            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {u.avatarUrl ? (
                              <Image
                                src={u.avatarUrl}
                                alt={u.fullName}
                                className="w-10 h-10 rounded-full object-cover"
                                type="user"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                <Icon name="User" size={20} color="var(--color-primary)" />
                              </div>
                            )}
                            <div className="text-right">
                              <p className="font-body font-medium text-foreground hover:text-primary transition-colors">{u.fullName}</p>
                              {u.isSuperAdmin && (
                                <span className="text-xs font-body text-primary">مشرف عام</span>
                              )}
                            </div>
                          </Link>
                        </td>
                        <td className="py-4 px-4 text-left">
                          <span className="font-body text-muted-foreground" dir="ltr">
                            {u.email}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium text-white ${
                                u.role === 'admin' ? 'bg-primary' : 'bg-muted'
                              }`}
                          >
                            {u.role === 'admin' ? 'مشرف' : 'مستخدم'}
                          </span>
                        </td>
                        {showManageActions && (
                          <td className="py-4 px-4 text-left">
                            {u.role === 'admin' && !u.isSuperAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="UserMinus"
                                iconPosition="left"
                                onClick={() => handleDemoteClick(u)}
                                disabled={demoteLoading}
                              >
                                إزالة
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      <ConfirmDialog
        isOpen={!!demoteTarget}
        onConfirm={handleDemoteConfirm}
        onCancel={handleDemoteCancel}
        title="إزالة من المشرفين"
        message={
          demoteTarget
            ? `هل أنت متأكد من إزالة ${demoteTarget.fullName} (${demoteTarget.email}) من المشرفين؟`
            : ''
        }
        confirmText="إزالة"
        cancelText="إلغاء"
        loading={demoteLoading}
      />
    </div>
  );
};

export default SettingsAdmins;
